import ReactSelectInput from "@pathwright/ui/src/components/form/form-select/ReactSelectInput"
import ReactSelectInputOption from "@pathwright/ui/src/components/form/form-select/ReactSelectInputOption"
import ReactSelectInputOptionLabel from "@pathwright/ui/src/components/form/form-select/ReactSelectInputOptionLabel"
import { useTranslate } from "@pathwright/ux/src/components/lng/useTranslate"
import { useEffect, useState } from "react"
import { FeatureKey, FeaturePermission } from "@pathwright/graphql-client/src/generated"
import { usePathwrightContext } from "../../pathwright/PathwrightContext"
import { canAdministrate } from "../../user/permissions"
import { getFeaturePermissionInfo } from "../utils"

const Option = (props: any) => {
  const { t } = useTranslate()
  const { featureKey } = props.selectProps
  const { icon, title, desc } = getFeaturePermissionInfo(
    featureKey,
    props.data.value,
    t
  )

  return (
    <ReactSelectInputOption
      {...props}
      icon={icon}
      label={title}
      descriptor={desc}
    />
  )
}

const OptionLabel = ({
  value,
  featureKey
}: {
  value: FeaturePermission
  featureKey: FeatureKey
}) => {
  const { t } = useTranslate()
  const { icon, title } = getFeaturePermissionInfo(featureKey, value, t)
  return <ReactSelectInputOptionLabel icon={icon} label={title} />
}

const FeatureVisibilitySelect = ({
  value: valueProp,
  onChange,
  featureKey,
  ...rest
}: {
  value?: FeaturePermission
  onChange?: (value: FeaturePermission) => void
  featureKey: FeatureKey
} & React.ComponentProps<typeof ReactSelectInput> & {}) => {
  const [value, setValue] = useState(valueProp || FeaturePermission.Everyone)
  const pwContext = usePathwrightContext()

  const options = [
    { value: FeaturePermission.Everyone },
    { value: FeaturePermission.StaffOnly },
    { value: FeaturePermission.AdminOnly }
  ]

  const availableOptions = options.filter(
    (option) =>
      option.value !== FeaturePermission.AdminOnly || canAdministrate(pwContext)
  )

  // upstream
  useEffect(() => {
    if (value && value !== valueProp) {
      onChange && onChange(value)
    }
  }, [value])

  // downstream
  useEffect(() => {
    if (valueProp && value !== valueProp) {
      setValue(valueProp)
    }
  }, [valueProp])

  return (
    <ReactSelectInput
      value={options.find((option) => option.value === value)}
      // NOTE: in order for formatOptionLabel to execute, options must be
      // a list of { value: any }
      formatOptionLabel={(props: { value: FeaturePermission }) => (
        <OptionLabel value={props.value} featureKey={featureKey} />
      )}
      onChange={(option: { value: FeaturePermission }) =>
        setValue(option.value)
      }
      options={availableOptions}
      components={{
        Option
      }}
      isSearchable={false}
      styles={{
        container: (base: any) => ({ ...base, minWidth: "100%" })
      }}
      featureKey={featureKey}
      {...rest}
    />
  )
}

FeatureVisibilitySelect.displayName = "FeatureVisibilitySelect"

export default FeatureVisibilitySelect
